/**
 * Shared financial calculation utilities
 * Ensures consistent calculation methods across all components
 */

/**
 * Normalizes a single cash flow item amount to monthly equivalent
 * @param {Object} item - Cash flow item with amount, recurrenceFrequency, recurrencePeriod
 * @returns {number} - Monthly amount in cents
 */
export const normalizeToMonthly = (item) => {
  if (!item || item.deleted || item.isPaid) {
    return 0;
  }

  const amount = item.amount || 0;

  // Only process recurring items with a valid recurrence frequency
  if (!item.recurrenceFrequency || item.recurrenceFrequency === 'none') {
    return 0;
  }

  const frequency = parseInt(item.recurrenceFrequency) || 1;

  switch (item.recurrencePeriod) {
    case 'days':
      // Simple approximation: 30 days per month
      return (amount * 30) / frequency;
    case 'weeks':
      // Simple approximation: 4 weeks per month
      return (amount * 4) / frequency;
    case 'months':
      return amount / frequency;
    case 'years':
      return amount / (12 * frequency);
    default:
      return 0;
  }
};

/**
 * Calculates monthly income from cash flow items with proper frequency normalization
 * Excludes one-time (non-recurring) items and paid items
 * @param {Array} items - Cash flow items
 * @returns {number} - Monthly income in cents
 */
export const calculateMonthlyIncome = (items) => {
  if (!Array.isArray(items)) {
    return 0;
  }

  return items.reduce((sum, item) => {
    if (item.type !== 'income') {
      return sum;
    }
    return sum + normalizeToMonthly(item);
  }, 0);
};

/**
 * Calculates monthly expenses from cash flow items with proper frequency normalization
 * Excludes one-time (non-recurring) items and paid items
 * @param {Array} items - Cash flow items
 * @returns {number} - Monthly expenses in cents
 */
export const calculateMonthlyExpenses = (items) => {
  if (!Array.isArray(items)) {
    return 0;
  }

  return items.reduce((sum, item) => {
    if (item.type !== 'expense') {
      return sum;
    }
    return sum + normalizeToMonthly(item);
  }, 0);
};

/**
 * Calculates monthly savings (income - expenses)
 * @param {number} monthlyIncome - Monthly income in cents
 * @param {number} monthlyExpenses - Monthly expenses in cents
 * @returns {number} - Monthly savings in cents
 */
export const calculateMonthlySavings = (monthlyIncome, monthlyExpenses) => {
  return monthlyIncome - monthlyExpenses;
};

/**
 * Calculates monthly savings rate as a percentage
 * @param {number} monthlyIncome - Monthly income in cents
 * @param {number} monthlyExpenses - Monthly expenses in cents
 * @returns {number} - Savings rate as percentage (0-100)
 */
export const calculateSavingsRate = (monthlyIncome, monthlyExpenses) => {
  if (monthlyIncome <= 0) {
    return 0;
  }
  const monthlySavings = calculateMonthlySavings(monthlyIncome, monthlyExpenses);
  return (monthlySavings / monthlyIncome) * 100;
};

/**
 * Calculates financial runway in months
 * @param {number} currentBalance - Current balance in cents
 * @param {number} monthlyExpenses - Monthly expenses in cents
 * @returns {number} - Runway in months (floor value)
 */
export const calculateRunway = (currentBalance, monthlyExpenses) => {
  if (monthlyExpenses <= 0) {
    return 0;
  }
  return Math.floor(currentBalance / monthlyExpenses);
};

/**
 * Calculates financial runway in days (more granular than months)
 * @param {number} currentBalance - Current balance in cents
 * @param {number} monthlyExpenses - Monthly expenses in cents
 * @returns {number} - Runway in days (floor value)
 */
export const calculateRunwayInDays = (currentBalance, monthlyExpenses) => {
  if (monthlyExpenses <= 0) {
    return 0;
  }
  // Use average days per month: 365.25 / 12 = 30.4375
  const dailyExpenses = monthlyExpenses / 30.4375;
  return Math.floor(currentBalance / dailyExpenses);
};

/**
 * Calculates spending breakdown by category using actual calendar occurrences
 * @param {Array} occurrences - Pre-generated occurrences from getItemsForDateRange
 * @param {Array} budgets - Budget categories for mapping budgetCategoryId to names
 * @param {Date|string} startDate - Start date of period (defaults to start of current month)
 * @param {Date|string} endDate - End date of period (defaults to end of current month)
 * @returns {Array} - Array of { emoji, name, total, actual, projected, count, percentage } sorted by total descending
 */
/**
 * Calculates spending breakdown by category using actual calendar occurrences
 *
 * IMPORTANT: This function expects occurrences to ALREADY include manual entries.
 * Manual entries are provided by getItemsForDateRange() to avoid duplication.
 * Do NOT re-extract manual entries from budgets.manualEntries.
 *
 * @param {Array} occurrences - Pre-generated occurrences from getItemsForDateRange (includes manual entries)
 * @param {Array} budgets - Budget categories for mapping budgetCategoryId to category metadata
 * @param {Date|string} startDate - Start date of period (defaults to start of current month)
 * @param {Date|string} endDate - End date of period (defaults to end of current month)
 * @returns {Array} - Array of category spending data with emoji, name, total, actual, projected, count, items, percentage
 */
export const calculateSpendingBreakdown = (occurrences, budgets = [], startDate = null, endDate = null) => {
  if (!Array.isArray(occurrences)) {
    return [];
  }

  // Import moment for date calculations (assumed to be available in the project)
  const moment = require('moment');

  // Default to current month if no dates provided
  const start = startDate ? moment(startDate).startOf('day') : moment().startOf('month');
  const end = endDate ? moment(endDate).endOf('day') : moment().endOf('month');
  const today = moment().startOf('day');

  // Process occurrences from getItemsForDateRange
  // Note: getItemsForDateRange returns amounts in DOLLARS, need to convert back to CENTS
  const processedOccurrences = occurrences
    .filter(occ => occ.type === 'expense' && !occ.deleted)
    .map(occ => {
      const occDate = moment(occ.date);
      // Calculate isActual: paid OR date has passed
      const isActual = occ.isPaid || occDate.isSameOrBefore(today);

      return {
        ...occ,
        amount: Math.round(occ.amount * 100), // Convert from dollars to cents
        isActual,
        date: occ.dueDate || moment(occ.date).format('YYYY-MM-DD'), // Ensure YYYY-MM-DD format
      };
    });

  // Include ALL occurrences (both paid and unpaid) for the breakdown
  // This gives a complete picture of spending for the period
  const allOccurrences = processedOccurrences;

  // Group expenses by budget category
  const categoryMap = {};
  let totalExpenses = 0;

  // Process each budget category
  budgets.forEach(budget => {
    if (!budget.id) return;

    const categoryId = budget.id;
    const categoryName = budget.title || budget.name || 'Uncategorized';
    const emoji = budget.emoji || '💰';
    const color = budget.color || '#9E9E9E'; // Default to gray if no color

    // Filter occurrences that belong to this budget category
    const categoryOccurrences = allOccurrences.filter(occ => {
      // Match by budgetCategoryId if available
      if (occ.budgetCategoryId) {
        return occ.budgetCategoryId === categoryId;
      }

      // Fallback to category name matching if no budgetCategoryId
      const itemCategory = (occ.category || '').toLowerCase();
      const searchCategory = categoryName.toLowerCase();

      // Direct match first
      if (itemCategory === searchCategory) return true;

      // Smart category matching using categoryKey field (for renamed categories)
      // Find the budget objects for both the search category and item category
      const searchBudget = budgets.find(b => b.title?.toLowerCase() === categoryName.toLowerCase());
      const itemBudget = budgets.find(b => b.id === item.budgetCategoryId);

      // If both have categoryKey, use semantic matching
      if (searchBudget?.categoryKey && itemBudget?.categoryKey) {
        if (searchBudget.categoryKey === itemBudget.categoryKey) return true;
      }

      // Legacy fallback: For Food category, also match Groceries (for old data)
      if (searchCategory === 'food' && (itemCategory.includes('groceries') || itemCategory.includes('food'))) return true;

      // Legacy fallback: For Groceries category, also match Food (for old data)
      if (searchCategory === 'groceries' && (itemCategory.includes('groceries') || itemCategory.includes('food'))) return true;

      // Partial match as fallback
      if (itemCategory.includes(searchCategory) || searchCategory.includes(itemCategory)) return true;

      return false;
    });

    // Note: Manual entries are already included in categoryOccurrences from getItemsForDateRange
    // No need to extract them again from budget.manualEntries to avoid duplication
    const allCategoryItems = categoryOccurrences;

    // If this category has any items (occurrences or manual entries), add it to the map
    if (allCategoryItems.length > 0) {
      const total = allCategoryItems.reduce((sum, item) => {
        return sum + (item.amount || 0);
      }, 0);

      // Calculate actual (paid or past) vs projected (unpaid and future) amounts
      const actual = allCategoryItems.reduce((sum, item) => {
        const isActual = item.isActual !== undefined ? item.isActual : true; // Manual entries are always actual
        return sum + (isActual ? (item.amount || 0) : 0);
      }, 0);

      const projected = allCategoryItems.reduce((sum, item) => {
        const isActual = item.isActual !== undefined ? item.isActual : true; // Manual entries are always actual
        return sum + (!isActual ? (item.amount || 0) : 0);
      }, 0);

      // Build subcategory breakdown from items
      const subcategoryMap = {};
      const budgetSubs = budget.subcategories || {};
      allCategoryItems.forEach(item => {
        const subId = item.subcategoryId;
        if (subId && budgetSubs[subId]) {
          if (!subcategoryMap[subId]) {
            subcategoryMap[subId] = {
              id: subId,
              title: budgetSubs[subId].title,
              emoji: budgetSubs[subId].emoji,
              total: 0,
            };
          }
          subcategoryMap[subId].total += item.amount || 0;
        }
      });

      categoryMap[categoryId] = {
        budgetId: categoryId, // Include budget ID for manual entry addition
        emoji,
        name: categoryName,
        color, // Include the budget category color
        total,
        actual,
        projected,
        count: allCategoryItems.length,
        subcategoryBreakdown: Object.values(subcategoryMap).sort((a, b) => b.total - a.total),
        items: categoryOccurrences.map(occ => ({
          id: occ.id,
          title: occ.title || occ.name || 'Unnamed Expense',
          emoji: occ.emoji || '💳',
          amount: occ.amount || 0,
          date: occ.date,
          category: occ.category || categoryName,
          recurrenceFrequency: occ.recurrenceFrequency || null,
          recurrencePeriod: occ.recurrencePeriod || null,
          isPaid: occ.isPaid,
          isActual: occ.isActual,
          isException: occ.isException || false,
          isManualEntry: occ.isManualEntry || false, // Preserve manual entry flag from getItemsForDateRange
        })),
      };

      totalExpenses += total;
    }
  });

  // Convert to array and calculate percentages
  const breakdown = Object.values(categoryMap).map(category => ({
    ...category,
    percentage: totalExpenses > 0
      ? (category.total / totalExpenses) * 100
      : 0,
  }));

  // Sort by total descending
  return breakdown.sort((a, b) => b.total - a.total);
};

/**
 * Calculates all financial metrics in one pass for efficiency
 * @param {Array} items - Cash flow items
 * @param {number} currentBalance - Current balance in cents
 * @returns {Object} - Object containing all calculated metrics
 */
export const calculateAllMetrics = (items, currentBalance) => {
  const monthlyIncome = calculateMonthlyIncome(items);
  const monthlyExpenses = calculateMonthlyExpenses(items);
  const monthlySavings = calculateMonthlySavings(monthlyIncome, monthlyExpenses);
  const savingsRate = calculateSavingsRate(monthlyIncome, monthlyExpenses);
  const runway = calculateRunway(currentBalance, monthlyExpenses);
  const spendingBreakdown = calculateSpendingBreakdown(items);

  return {
    monthlyIncome,
    monthlyExpenses,
    monthlySavings,
    savingsRate,
    runway,
    spendingBreakdown,
  };
};

/**
 * Calculates financial metrics for a specific month period
 * Includes all actual occurrences (recurring + one-time + manual entries)
 * @param {Array} items - Cash flow items
 * @param {Array} budgets - Budget categories with manual entries
 * @param {Date|string} startDate - Start date of the month (defaults to start of current month)
 * @param {Date|string} endDate - End date of the month (defaults to end of current month)
 * @returns {Object} - Monthly metrics snapshot
 */
export const calculateMonthMetrics = (items, budgets = [], startDate = null, endDate = null) => {
  const moment = require('moment');

  // Default to current month if no dates provided
  const start = startDate ? moment(startDate).startOf('day') : moment().startOf('month');
  const end = endDate ? moment(endDate).endOf('day') : moment().endOf('month');

  // Calculate income for the period (actual occurrences)
  let monthlyIncome = 0;
  items.forEach(item => {
    if (item.type !== 'income' || item.deleted) return;

    // For recurring income items, generate occurrences
    if (item.recurrenceFrequency && item.recurrenceFrequency !== 'none') {
      let currentDate = moment(item.date);
      const frequency = parseInt(item.recurrenceFrequency) || 1;

      while (currentDate.isSameOrBefore(end)) {
        if (currentDate.isBetween(start, end, 'day', '[]')) {
          const dueDate = currentDate.format('YYYY-MM-DD');
          const exception = item.occurrenceExceptions?.[dueDate];

          // Skip deleted exceptions
          if (!exception?.deleted) {
            monthlyIncome += exception?.amount || item.amount;
          }
        }

        // Advance to next occurrence
        switch (item.recurrencePeriod) {
          case 'days':
            currentDate.add(frequency, 'days');
            break;
          case 'weeks':
            currentDate.add(frequency, 'weeks');
            break;
          case 'months':
            currentDate.add(frequency, 'months');
            break;
          case 'years':
            currentDate.add(frequency, 'years');
            break;
          default:
            currentDate = moment(end).add(1, 'day');
        }
      }
    }
    // For one-time income items
    else if (moment(item.date).isBetween(start, end, 'day', '[]')) {
      monthlyIncome += item.amount || 0;
    }
  });

  // Calculate expenses using existing spendingBreakdown (includes manual entries)
  const spendingByCategory = calculateSpendingBreakdown(items, budgets, start, end);
  const monthlyExpenses = spendingByCategory.reduce((sum, cat) => sum + cat.total, 0);
  const monthlySavings = monthlyIncome - monthlyExpenses;
  const savingsRate = monthlyIncome > 0 ? (monthlySavings / monthlyIncome) * 100 : 0;

  // Create period key (YYYY-MM format)
  const periodKey = start.format('YYYY-MM');

  return {
    periodKey,
    startDate: start.format('YYYY-MM-DD'),
    endDate: end.format('YYYY-MM-DD'),
    timestamp: new Date().toISOString(),
    monthlyIncome,
    monthlyExpenses,
    monthlySavings,
    savingsRate,
    spendingByCategory: spendingByCategory.map(cat => ({
      budgetId: cat.budgetId,
      categoryName: cat.name,
      emoji: cat.emoji,
      color: cat.color,
      totalSpent: cat.total,
      itemCount: cat.count,
      percentage: cat.percentage,
    })),
    totalExpenseOccurrences: spendingByCategory.reduce((sum, cat) => sum + cat.count, 0),
  };
};

/**
 * Compares two monthly metric snapshots
 * @param {Object} currentMonth - Current month metrics
 * @param {Object} previousMonth - Previous month metrics
 * @returns {Object} - Comparison with deltas and percentage changes
 */
export const compareMonthlyMetrics = (currentMonth, previousMonth) => {
  // If no previous month data, return null for all comparisons
  if (!previousMonth) {
    return {
      incomeDelta: null,
      incomePercent: null,
      expensesDelta: null,
      expensesPercent: null,
      netDelta: null,
      netPercent: null,
      categoryChanges: [],
      hasData: false,
    };
  }

  // Calculate deltas
  const incomeDelta = currentMonth.monthlyIncome - previousMonth.monthlyIncome;
  const expensesDelta = currentMonth.monthlyExpenses - previousMonth.monthlyExpenses;
  const netDelta = currentMonth.monthlySavings - previousMonth.monthlySavings;

  // Calculate percentage changes (avoid division by zero)
  const incomePercent = previousMonth.monthlyIncome > 0
    ? (incomeDelta / previousMonth.monthlyIncome) * 100
    : null;
  const expensesPercent = previousMonth.monthlyExpenses > 0
    ? (expensesDelta / previousMonth.monthlyExpenses) * 100
    : null;
  const netPercent = previousMonth.monthlySavings !== 0
    ? (netDelta / Math.abs(previousMonth.monthlySavings)) * 100
    : null;

  // Compare categories
  const categoryChanges = [];
  const currentCategories = currentMonth.spendingByCategory || [];
  const previousCategories = previousMonth.spendingByCategory || [];

  // Create a map of previous categories for quick lookup
  const prevCategoryMap = {};
  previousCategories.forEach(cat => {
    prevCategoryMap[cat.budgetId] = cat;
  });

  currentCategories.forEach(currentCat => {
    const prevCat = prevCategoryMap[currentCat.budgetId];

    if (prevCat) {
      const delta = currentCat.totalSpent - prevCat.totalSpent;
      const percent = prevCat.totalSpent > 0
        ? (delta / prevCat.totalSpent) * 100
        : null;

      categoryChanges.push({
        budgetId: currentCat.budgetId,
        categoryName: currentCat.categoryName,
        emoji: currentCat.emoji,
        currentTotal: currentCat.totalSpent,
        previousTotal: prevCat.totalSpent,
        delta,
        percent,
      });
    } else {
      // New category this month
      categoryChanges.push({
        budgetId: currentCat.budgetId,
        categoryName: currentCat.categoryName,
        emoji: currentCat.emoji,
        currentTotal: currentCat.totalSpent,
        previousTotal: 0,
        delta: currentCat.totalSpent,
        percent: null,
        isNew: true,
      });
    }
  });

  return {
    incomeDelta,
    incomePercent,
    expensesDelta,
    expensesPercent,
    netDelta,
    netPercent,
    categoryChanges,
    hasData: true,
  };
};
